import { useContext, useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from 'react-redux';
import { bindActionCreators } from 'redux';
import { actionCreators } from '../../redux/index';
import PageContext from "./PageContext";
import { HttpContext } from '../../api-services/HttpService';
import ApiRequestButton from './ApiRequestButton';
import applyFormat from '../functions/applyFormat'; 
import DynamicPageInput from "./DynamicPageInputCopy.jsx";
import dynamicValidation from "../functions/dynamicValidation";
import dynamicTransformation from "../functions/dynamicTransformation";
import validateInputArray from "../functions/validateInputArray";
import CCOffcanvas from "./CCOffcanvas.tsx";
import ButtonComponent from "./ButtonComponent.jsx";
import standardFilter from "../functions/standardFilter.js";
import applyFilters from "../functions/applyFilters.js";
import isNullOrUndefined from "../functions/isNullOrUndefined.js"; 

const EditCompanyDetailsModal = () =>{
    // get context variables from parent
    const { 
        editCompanyDetailsModal, 
        setEditCompanyDetailsModal,
        masterCompanyData, 
        getCompanyDetailsData,  
        snackbarAlert,
        processorDefaultData, 
        consortiumFlag, 
        masterCompanyLevelConfig, 
        masterCompanyLevelLists, 
        setMasterCompanyLevelLists,
        fullIncentiveFreqList, 
        setInfoModalData
    } = useContext(PageContext);
    // initialize http context 
    const {
        PUT, 
    } = useContext(HttpContext);
    // get persistant redux global state from store
    const company = useSelector((state) => state.company);
    const companyInfo = useSelector((state) => state.companyInfo); 
    // initialize reducers to update redux global state
    const dispatch = useDispatch();
    const { setCompany, setCompanyInfo } = bindActionCreators(actionCreators, dispatch);
    // initialize state
    const [values, setValues] = useState({}); 
    const [config, setConfig] = useState([]); 
    const [error, setError] = useState([{}]);
    // initailize flag to determine incentiveId change 
    const incentiveIdChange = useRef(false); 
    // initialize ref for initial render 
    const initialRender1 = useRef(true); 

    // event handler for submit company details click
    const onSubmitClick = () =>{
        return new Promise((resolve, reject) => {
            // destructure values to pass by value
            let requestValues = [{...values}]; 
            // custom block on changing between consortium and non-consortium
            for (let i = 0; i < processorDefaultData.length; i++){
                if (requestValues[0]['incentiveid']['id'] == processorDefaultData[i]['id'] && consortiumFlag != processorDefaultData[i]['consortium']){
                    snackbarAlert(true, 'Please contact Argus Support to switch portfolio between Consortium and Non-Consortium.', 'error'); 
                    reject();
                    return; 
                }
            }
            // validate input
            let checkError = validateInputArray(
                requestValues, 
                masterCompanyLevelConfig,
                dynamicValidation,
                masterCompanyLevelLists, 
                snackbarAlert,  
                error, 
                setError, 
                (row, config, args) => {
                    let temp = row; 
                    console.log("TEMP1: ", {...temp});
                    // run dynamic transformation 
                    temp = dynamicTransformation(row, config, args); 
                    console.log("TEMP2: ", {...temp});
                    // remove all keys not in config
                    Object.keys(temp).map(key => {
                        let found = false; 
                        for (let i = 0; i < config.length; i++) {
                            if (config[i]['columnid'] == key){
                                found = true; 
                                break; 
                            }
                        }
                        // remove key if not found
                        if (!found && key !== 'annualized') { // must include annualized for case when client does not edit it
                            delete temp[key]; 
                        }
                    }); 
                    console.log("TEMP3: ", {...temp}); 
                    // return row on success
                    return temp; 
                }, 
                {},
            ); 
            // exit request if error 
            if (checkError) {
                reject(); 
                return; 
            }
            // get oldValues to attach to request 
            let temp = {...masterCompanyData}
            let oldValues = applyFormat(temp, masterCompanyLevelConfig, masterCompanyLevelLists, null); 
            oldValues = dynamicTransformation(oldValues, masterCompanyLevelConfig, {}); 

            // args for request 
            let args = {
                ...requestValues[0], 
                mastercompanyid: masterCompanyData['mastercompanyid'], 
                oldValues: oldValues
            }
            console.log('ARGS: ', args); 
            // make put request to update data in aurora db
            let response; 
            PUT('/client/companyDetailsData', args).then(res => {
                console.log(res);
                response = res; 
                // set company to new company name
                setCompany(requestValues[0]['mastercompanyname']);
                // hide modal on success
                setEditCompanyDetailsModal(editCompanyDetailsModal => editCompanyDetailsModal = false);
                // trigger success alert
                snackbarAlert(true, 'Successfully uploaded data.', 'success');
                // refresh CompanyDetailsPage using function from parent
                return getCompanyDetailsData(requestValues[0]['mastercompanyname']);
            }).then(res => {
                console.log(res);
                // resolve on success
                resolve(response); 
            }).catch(err => {
                console.log(err);
                // show alert on error
                snackbarAlert(true, 'Error uploading data.', 'error');
                // reject on error
                reject(err); 
            });
        }); 
    }

    // useEffect to run on config change 
    useEffect(() => {
        if (masterCompanyLevelConfig){
            setConfig(config => config = masterCompanyLevelConfig); 
        }
    }, [masterCompanyLevelConfig]); 

    // useEffect to run on masterCompanyData change
    useEffect(() => { 
        if (masterCompanyData && incentiveIdChange.current === false) {
            // add ids to lookup values 
            let temp = {
                ...masterCompanyData // destructure to pass by value 
            }; 
            temp = applyFormat(temp, masterCompanyLevelConfig, masterCompanyLevelLists, null); 
            // set state 
            setValues(temp); 
        }
    }, [masterCompanyData, masterCompanyLevelConfig, masterCompanyLevelLists]); 

    // useEffect to filter incentive frequency choices 
    useEffect(() => {
        // filter incentive frequency list 
        if (initialRender1.current == false && values.incentiveid?.id) {
            // specify function list for filters
            const functionList = [
                {function: standardFilter, value: values.incentiveid?.id, flag: 1, cols: ['incentiveid']}
            ];
            // use applyFilters function to search by running all functions declared above
            let filteredList2 = applyFilters(fullIncentiveFreqList.current, functionList); 
            // update rebate config 
            setMasterCompanyLevelLists(prev => ({
                ...prev, 
                incentivefreq: filteredList2
            })) ;
            // clear frequency if masterCompanyLevelValues?.incentiveid changes
            // reset dependent fields to null (not removed) so the Select stays controlled 
            setValues(prev => ({...prev, incentivefreq: null, annualized: null}));  
        }
        else {
            initialRender1.current = false; 
        }
        // if both incentiveids are populated check for change
        if (values.incentiveid?.incentiveid && masterCompanyData.incentiveid) {
            // set incentiveIdChange if necessary
            if (values.incentiveid?.incentiveid === masterCompanyData.incentiveid) {
                incentiveIdChange.current = false; 
            }
            else {
                incentiveIdChange.current = true; 
            }
        }

    }, [values.incentiveid]); 

    // set default for annualize on incentive id change
    useEffect(() => {
        // check that incentiveid or incentivefreq have been changed
        if (values.incentiveid?.incentiveid !== masterCompanyData.incentiveid
            || values.incentivefreq?.label !== masterCompanyData.incentivefreq
        ) {
            // assign default if annualized is allowed 
            if (values.incentivefreq?.allow === true 
            && !isNullOrUndefined(values.incentivefreq?.default)) {
                setValues(prev => ({
                    ...prev, 
                    annualized: values.incentivefreq?.default
                })); 
            }
            // false if not allowed or no default
            else {
                setValues(prev => ({
                    ...prev, 
                    annualized: false
                })); 
            }
        }
        // if no change, set annualized to original
        else {
            setValues(prev => ({
                ...prev, 
                annualized: masterCompanyData?.annualized
            })); 
        }
        
    }, [values.incentivefreq]);

    // useEffect(() => {
    //     console.log(values); 
    // }, [values]); 

    return( 
        <> 
            <CCOffcanvas
                title="Edit Company Details"
                show={editCompanyDetailsModal}
                onCanvasClose={()=>setEditCompanyDetailsModal(editCompanyDetailsModal => editCompanyDetailsModal = false)}
                bodyCssClassName="offcanvas-body bg-white p-3 pt-2"
                offCanvasClassName="offcanvas-parent-card-30"
                >
                <div className="d-flex flex-column h-100">
                    <div className="d-flex flex-column gap-3 flex-grow-1 overflow-scroll" >
                        {config.map((el) => 
                            <div>
                                <DynamicPageInput 
                                    data={values} 
                                    setData={setValues} 
                                    config={el} 
                                    args={masterCompanyLevelLists} 
                                    width={'100%'} 
                                    showError={error[0][el['columnid']]}
                                    topAlignTypography={true}
                                />
                            </div>
                        )}
                    </div>
                    {/* button to submit company details */}
                    <div
                        className="d-flex justify-content-end p-3 w-100 offcanvas-footer z-2" style={{height: '65px'}}
                    >
                        <ButtonComponent tag={"Cancel"} 
                            border={'#fcd800 1px solid'} background={'#fff'} color={'#001317'} 
                            onButtonClick={()=>setEditCompanyDetailsModal(editCompanyDetailsModal => editCompanyDetailsModal = false)}
                        />
                        <ApiRequestButton onButtonClick={onSubmitClick} />
                    </div>
                </div>
            </CCOffcanvas>
        </>
    );
}

export default EditCompanyDetailsModal;